import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { AbstractControl } from '@angular/forms'

import { isEmpty } from 'lodash'

import { Subscription, merge } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxButtonComponent, FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  ButtonTemplateConfig,
  LabelAccessibilityProps,
  ProcessActionTriggerEvent,
  ResolvedAction,
  getActionsByTemplateConfigAndTrigger$,
  getCurrentReadOnlyState,
  isCurrentNode$,
  isUiCallAction,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { FlxFormsService } from '../../services/flx-forms.service'
import { getBoolExpressionSignal, getContentIcon, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-button-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [FlxButtonComponent, FlxLocalizePipe, FlxThemeModule, FlxStyleModule, FlxIconModule],
  templateUrl: './button-wrapper.component.html',
  styleUrl: './button-wrapper.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxButtonWrapperComponent implements OnInit, AfterViewInit {
  #formService = inject(FlxFormsService)
  #actionService = inject(FlxActionService)
  #injector = inject(Injector)
  #destroyRef = inject(DestroyRef)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])
  // tracks the form-submit valueChanges subscription so it can be torn down and
  // re-established whenever the resolved click actions change.
  #formSubmitSub: Subscription | null = null

  templateConfig = input.required<ButtonTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  accessibility = computed((): LabelAccessibilityProps | null => this.flowxProps()?.accessibility)

  icons = computed(() => {
    const { icon } = this.flowxProps()
    return { icon: getContentIcon(icon) }
  })

  readonly = getCurrentReadOnlyState

  buttonDisabled = computed(
    () =>
      ((this.formControlsPristine() && !this.isCurrent?.()) || this.isDisabled?.()) &&
      !this.readonly()
  )

  formControlsPristine = signal(false)

  // expressions
  isDisabled: Signal<boolean>
  isHidden: Signal<boolean>
  // dynamic props
  label: Signal<string>
  ariaLabel: Signal<string>
  // current node
  isCurrent: Signal<boolean>

  getFormControlsToValidate(
    formsToSubmit: string[],
    reusableContext: string = null
  ): {
    control: AbstractControl
    name: string
  }[] {
    return this.#formService.getFormControlsAndNames(
      formsToSubmit,
      this.templateConfig().prototypeInstanceMeta,
      reusableContext
    )
  }

  isPristine(formsToSubmit: string[], reusableContext: string = null): boolean {
    return this.getFormControlsToValidate(formsToSubmit, reusableContext).every(
      ({ control }) => control.pristine
    )
  }

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const { processInstanceUuid, nodeDefinitionId, context } = this.templateConfig()
      const { label, disabled, hide } = this.flowxProps()
      this.isDisabled = getBoolExpressionSignal(disabled, this.templateConfig(), {
        logType: 'disabled',
      })
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.label = getDynamicPropSignal(label, this.templateConfig())
      this.ariaLabel = getDynamicPropSignal(this.accessibility()?.label, this.templateConfig())

      this.isCurrent = toSignal(isCurrentNode$(processInstanceUuid, nodeDefinitionId, context))

      // Re-register the form-submit tracking whenever the resolved click actions
      // change (e.g. they land after this component mounts on a wizard advance).
      effect((onCleanup) => {
        const uiAction = this.#clickActions().find(({ action }) => isUiCallAction(action))?.action
        this.registerSubmitFormChanges(
          uiAction?.params?.formsToSubmit,
          uiAction?.reusableUiTemplateContext
        )
        onCleanup(() => {
          this.#formSubmitSub?.unsubscribe()
          this.#formSubmitSub = null
        })
      })
    })
  }

  registerSubmitFormChanges(formsToSubmit: any[], reusableContext: string = null): void {
    if (isEmpty(formsToSubmit)) {
      // No form to validate, so we need to allow the user to execute the action again
      return
    }

    this.formControlsPristine.set(this.isPristine(formsToSubmit, reusableContext))

    this.#formSubmitSub = merge(
      ...this.getFormControlsToValidate(formsToSubmit, reusableContext).map(
        ({ control }) => control.valueChanges
      )
    ).subscribe(() =>
      this.formControlsPristine.set(this.isPristine(formsToSubmit, reusableContext))
    )
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  async onClick(): Promise<void> {
    await this.#actionService.runActions(this.#clickActions(), this.templateConfig())
  }
}
